import { useLocalization } from "@fluent/react";
import { ContentCard, Row } from "@narmi/design_system";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { PageContent } from "@lending/components/PageContent";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { useListApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useListApplicationApplicantDetailsQuery";
import { usePutApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/usePutApplicationApplicantDetailsQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation/hooks/useFlowNavigation";
import { APPLICATION_RELATIONSHIP } from "@lending/types";
import styles from "./CoApplicantAddressAsk.module.css";
import { useStore } from "../../../../contexts/StoreContext";

function CoApplicantAddressAsk() {
  const { l10n } = useLocalization();
  const { updateData, data: storeData } = useStore();
  const { goBack, goNext } = useFlowNavigation();

  const { showGenericErrorToast } = useToastsContext();
  const { institution, isLoading: isInstitutionLoading } = useGetInstitutionQuery();
  const askMailingAddress = Boolean(institution.lending_ask_mailing_address);

  const { data, isLoading } = useListApplicationApplicantDetailsQuery();

  const primaryApplicant = data?.results.find(
    (applicant) => applicant.application_relationship === APPLICATION_RELATIONSHIP.PRIMARY
  );
  const coApplicant = data?.results.find(
    (applicant) => applicant.application_relationship === APPLICATION_RELATIONSHIP.CO_APPLICANT
  );

  const coApplicantName = coApplicant?.first_name ?? "";

  const { mutateAsync: updateCoApplicantAddress, isPending } =
    usePutApplicationApplicantDetailsQuery({
      onSuccess: () => {
        updateData({ hasSeparateCoApplicantAddress: false });
        goNext({ hasSeparateCoApplicantAddress: false });
      },
      onError: () => {
        showGenericErrorToast();
      },
    });

  const { mutateAsync: clearCoApplicantAddress, isPending: isClearingAddress } =
    usePutApplicationApplicantDetailsQuery({
      onError: () => {
        showGenericErrorToast();
      },
    });

  async function onClickYes() {
    if (!coApplicant || !primaryApplicant?.residential_address) {
      showGenericErrorToast();
      return;
    }

    await updateCoApplicantAddress({
      params: {
        path: {
          uuid: coApplicant.id,
        },
      },
      body: {
        ...coApplicant,
        residential_address: {
          ...primaryApplicant.residential_address,
        },
        ...(askMailingAddress
          ? {
              mailing_address: {
                ...(primaryApplicant.mailing_address ?? primaryApplicant.residential_address),
              },
            }
          : {}),
      },
    });
  }

  async function onClickNo() {
    // Only clear the address if it was auto-copied from the primary applicant via the
    // "Yes" path (hasSeparateCoApplicantAddress === false). A manually-entered separate
    // address (previous answer "No") must be preserved — otherwise re-confirming "No"
    // after going back wipes the address the user already entered. See LNDG-1349.
    if (coApplicant?.residential_address && storeData.hasSeparateCoApplicantAddress === false) {
      try {
        await clearCoApplicantAddress({
          params: { path: { uuid: coApplicant.id } },
          body: {
            ...coApplicant,
            residential_address: null,
            ...(askMailingAddress ? { mailing_address: null } : {}),
          },
        });
      } catch {
        // If clearing fails, still proceed
      }
    }
    updateData({ hasSeparateCoApplicantAddress: true });
    goNext({ hasSeparateCoApplicantAddress: true });
  }

  function onClickBack() {
    goBack();
  }

  return (
    <PageLayout isLoading={isLoading || isInstitutionLoading}>
      <PageContent>
        <PageHeader
          heading={
            askMailingAddress
              ? l10n.getString(
                  "co-applicant-address-ask-heading-with-mailing",
                  { name: coApplicantName },
                  `Does ${coApplicantName} have the same address info as you?`
                )
              : l10n.getString(
                  "co-applicant-address-ask-heading",
                  { name: coApplicantName },
                  `Does ${coApplicantName} live with you?`
                )
          }
          subheading=""
        />
        <div className={styles.optionCards}>
          <ContentCard
            kind="button"
            onClick={onClickYes}
            disabled={isPending || isClearingAddress}
            radiusSize="m"
          >
            <div className={styles.cardContent}>
              <div className={styles.cardIcon}>
                <span className="narmi-icon-home fontSize--l" />
              </div>
              <div className="fontSize--m fontWeight--bold">
                {askMailingAddress
                  ? l10n.getString(
                      "co-applicant-address-ask-yes-with-mailing",
                      null,
                      "Yes, they have the same address info"
                    )
                  : l10n.getString("co-applicant-address-ask-yes", null, "Yes, they live with me")}
              </div>
            </div>
          </ContentCard>
          <ContentCard
            kind="button"
            onClick={onClickNo}
            disabled={isPending || isClearingAddress}
            radiusSize="m"
          >
            <div className={styles.cardContent}>
              <div className={styles.cardIcon}>
                <span className="narmi-icon-map-pin fontSize--l" />
              </div>
              <div className="fontSize--m fontWeight--bold">
                {askMailingAddress
                  ? l10n.getString(
                      "co-applicant-address-ask-no-with-mailing",
                      null,
                      "No, they have different address info"
                    )
                  : l10n.getString(
                      "co-applicant-address-ask-no",
                      null,
                      "No, they don't live with me"
                    )}
              </div>
            </div>
          </ContentCard>
        </div>
        <div className="margin--top--xl">
          <Row alignItems="center" justifyContent="space-between">
            <BackButton onClick={onClickBack} />
          </Row>
        </div>
      </PageContent>
    </PageLayout>
  );
}

export { CoApplicantAddressAsk };
